import { createBrowserRouter, Outlet, useOutletContext, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@shared/contexts/LanguageContext';
import PageLayout from '@shared/layouts/PageLayout';
import ScrollToTop from '@shared/components/ScrollToTop';
import { getUserInfo } from '@shared/api/client';
import { ROUTES } from './routes';
import ErrorPage from '@shared/pages/ErrorPage';
import MaintenancePage from '@shared/pages/MaintenancePage';
import { MAINTENANCE_MODE, FEATURE_FLAGS } from '@shared/config/featureFlags';
import { checkMemberNotification } from '@features/auth/api/authService';
import MemberNotificationBanner from '@shared/components/MemberNotificationBanner';
import FallingRewardMount from '@shared/components/falling-petals/FallingRewardMount';

// Home Feature
import LandingPage from '@features/home/pages/LandingPage';

// Auth Feature
import LoginPage from '@features/auth/pages/LoginPage';
import RegisterPage from '@features/auth/pages/RegisterPage';

// Lamp Lighting Feature
import WisdomLampPage from '@features/lamp-lighting/pages/WisdomLampPage';
import VideoCollectionPage from '@features/video-collection/pages/VideoCollectionPage';
import LampLightingFormPage from '@features/lamp-lighting/pages/LampLightingFormPage';
import LampBulletinPage from '@features/lamp-lighting/pages/LampBulletinPage';
import BlessingResultPage from '@features/lamp-lighting/pages/BlessingResultPage';

// Branch Reports Feature
import BranchReportsPage from '@features/branch-reports/pages/BranchReportsPage';
import BranchReportDetailPage from '@features/branch-reports/pages/BranchReportDetailPage';

// Latest News Feature
import LatestNewsDetailPage from '@features/latest-news/pages/LatestNewsDetailPage';

// Postcard Feature
import PostcardCreatePage from '@features/postcard/pages/PostcardCreatePage';
import PostcardViewPage from '@features/postcard/pages/PostcardViewPage';
import PostcardMyPage from '@features/postcard/pages/PostcardMyPage';

// Profile Feature（個人資訊）
import ProfilePage from '@features/profile/pages/ProfilePage';
// 心理測驗護法DNA
import DnaListPage from '@/features/dna/pages/DnaListPage';
import DnaQuestionPage from '@/features/dna/pages/DnaQuestionPage';
import DnaAnswerPage from '@features/dna/pages/DnaAnswerPage';

// 教理問答
import QuizListPage from '@/features/quiz/pages/QuizListPage';
import QuizQuestionPage from '@/features/quiz/pages/QuizQuestionPage';
import QuizAnswerPage from '@/features/quiz/pages/QuizAnswerPage';

// 禪心連線
import BoardListPage from '@features/live-comment/pages/BoardListPage';
import TopicListPage from '@features/live-comment/pages/TopicListPage';
import CommentBoardPage from '@features/live-comment/pages/CommentBoardPage';

// QR Code活動簽到
import QrcodePage from '@features/qr-code/pages/QrcodePage';
// 兌換
import RedeemPage from '@/features/redeem/pages/RedeemPage';
import RulePages from '@features/rule/pages/RulePage';

// 覺光庭影
import JueGuangLandingPage from '@features/jue-guang/pages/JueGuangLandingPage';
import JueGuangPage from '@features/jue-guang/pages/JueGuangPage';
import JueGuangDetailPage from '@features/jue-guang/pages/JueGuangDetailPage';

// 中台心傳
import TransmissionListPage from '@features/transmission/pages/TransmissionListPage';
import TransmissionDetailPage from '@features/transmission/pages/TransmissionDetailPage';
import TransmissionAreaPage from '@features/transmission/pages/TransmissionAreaPage';

// 佛法填字遊戲
import WordPuzzlePage from '@features/word-puzzle/pages/WordPuzzlePage';

// 發願樹
import WishTreePage from '@features/wish-tree/pages/WishTreePage';

// 護博會
import GuardianExpoPage from '@features/guardian-expo/pages/GuardianExpoPage';
import GuardianExpoLecturePage from '@features/guardian-expo/pages/GuardianExpoLecturePage';
import GuardianExpoMarketPage from '@features/guardian-expo/pages/GuardianExpoMarketPage';
import GuardianExpoChallengePage from '@features/guardian-expo/pages/GuardianExpoChallengePage';
import GuardianExpoDashboardPage from '@features/guardian-expo/pages/GuardianExpoDashboardPage';

// 飄落特效測試頁（開發用）
import FallingPetalsDemoPage from '@shared/pages/FallingPetalsDemoPage';

// PageLayout Wrapper with ScrollToTop and Auth State
const PageLayoutWrapper = () => {
  const [user, setUser] = useState(() => getUserInfo());
  const [layoutOptions, setLayoutOptions] = useState({});
  const [memberNotification, setMemberNotification] = useState('');
  const [redeemOpen, setRedeemOpen] = useState(false);
  const navigate = useNavigate();
  const prevIsLoggedIn = useRef(!!getUserInfo());
  const { t } = useLanguage();

  // Check auth on mount and listen for updates
  useEffect(() => {
    const checkAuth = () => {
      const newUser = getUserInfo();
      const newIsLoggedIn = !!newUser;
      setUser(newUser ?? null);
      if (newIsLoggedIn !== prevIsLoggedIn.current) {
        prevIsLoggedIn.current = newIsLoggedIn;
        if (!FEATURE_FLAGS.MEMBER_NOTIFICATION.enabled) return;
        checkMemberNotification().then(({ show_notification, notification }) => {
          setMemberNotification(show_notification && notification ? notification : '');
        }).catch(() => { });
      }
    };

    window.addEventListener('auth-change', checkAuth);
    return () => window.removeEventListener('auth-change', checkAuth);
  }, []);

  const location = useLocation();
  useEffect(() => {
    try {
      const history = JSON.parse(sessionStorage.getItem('pathHistory') || '[]');
      if (history[history.length - 1] !== location.pathname) {
        history.push(location.pathname);
        if (history.length > 5) history.shift();
        sessionStorage.setItem('pathHistory', JSON.stringify(history));
      }
    } catch (e) {
      console.error('Failed to update pathHistory:', e);
    }
  }, [location.pathname]);

  // 會員通知：mount 時呼叫一次
  useEffect(() => {
    if (!FEATURE_FLAGS.MEMBER_NOTIFICATION.enabled) return;
    checkMemberNotification().then(({ show_notification, notification }) => {
      setMemberNotification(show_notification && notification ? notification : '');
    }).catch(() => { });
  }, []);

  const notificationBanner = <MemberNotificationBanner message={memberNotification} onDismiss={() => setMemberNotification('')} />;

  const bottomNavItems = [
    { id: 'home', label: t('bottomNav.home'), path: ROUTES.HOME },
    { id: 'video', label: t('bottomNav.videos'), path: ROUTES.VIDEO_COLLECTION, disabled: !FEATURE_FLAGS.VIDEO_COLLECTION.enabled },
    { id: 'redeem', label: t('bottomNav.redeem'), path: ROUTES.REDEEM },
    { id: 'scan', label: t('bottomNav.scan'), path: ROUTES.QR_CODE },
  ];

  const handleBottomNavClick = (item) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <PageLayout
      headerProps={{ user, showHome: true, subMenu: layoutOptions.headerSubMenu ?? (layoutOptions.showNotification ? notificationBanner : null) }}
      bottomNavProps={{ navItems: bottomNavItems, onNavClick: handleBottomNavClick }}
      hideHeader={layoutOptions.hideHeader ?? false}
      hideFooter={layoutOptions.hideFooter ?? false}
      hideBottomNav={layoutOptions.hideBottomNav ?? false}
      noPaddingTop={layoutOptions.noPaddingTop ?? false}
      noPaddingBottom={layoutOptions.noPaddingBottom ?? false}
      headerHeight={layoutOptions.headerHeight ?? 70}
    >
      <ScrollToTop />
      <Outlet context={{ user, setLayoutOptions }} />
      {/* <RulePages isOpen={redeemOpen} onClose={() => setRedeemOpen(false)} /> */}
      <FallingRewardMount />
    </PageLayout>
  );
};

// Hook for child pages to opt-out of layout elements
export function usePageLayout() {
  return useOutletContext();
}

// 需要登入才能進入的頁面，未登入導向註冊頁
function RequireAuth({ children }) {
  const location = useLocation();
  if (!getUserInfo()) {
    return <Navigate to={ROUTES.LOGIN} state={{ redirectTo: location.pathname }} replace />;
  }
  return children;
}

const router = MAINTENANCE_MODE
  ? createBrowserRouter([{ path: '*', element: <MaintenancePage /> }])
  : createBrowserRouter([
    {
      path: '/',
      errorElement: <ErrorPage />,
      children: [
        {
          path: ROUTES.HOME,
          element: <LandingPageWrapper />,
        },
        {
          path: ROUTES.LANDING,
          element: <LandingPageWrapper />,
        },

        // 精舍報導詳情 — 無 PageLayout（banner 貼頂，自帶 UI）
        {
          path: ROUTES.BRANCH_REPORT_DETAIL_PATTERN,
          element: <BranchReportDetailPage />,
        },

        // 最新消息內頁 — 無 PageLayout（自帶 UI）
        {
          path: ROUTES.NEWS_DETAIL_PATTERN,
          element: <LatestNewsDetailPage />,
        },

        // 明信片公開查看 — 無 PageLayout（任何人皆可查看）
        {
          path: ROUTES.POSTCARD_VIEW_PATTERN,
          element: <PostcardViewPage />,
        },

        // 覺光庭影 record 詳情 — 無 PageLayout（全版沉浸式）
        {
          path: ROUTES.JUE_GUANG_RECORD_PATTERN,
          element: <JueGuangDetailPage />,
        },

        // 佛法填字遊戲 — 無 PageLayout（全版沉浸式）
        {
          path: ROUTES.WORD_PUZZLE,
          element: <WordPuzzlePage />,
        },

        // 飄落特效測試頁 — 無 PageLayout（開發用，全版展示）
        {
          path: ROUTES.FALLING_PETALS_DEMO,
          element: <FallingPetalsDemoPage />,
        },

        // 護博會整合儀表板 — 無 PageLayout（大螢幕看板，全版沉浸式）
        {
          path: ROUTES.GUARDIAN_EXPO_DASHBOARD,
          element: <GuardianExpoDashboardPage />,
        },

        // 中台心傳詳情 — 無 PageLayout（自帶 header/nav）
        {
          path: ROUTES.TRANSMISSION_DETAIL_PATTERN,
          element: <TransmissionDetailPage />,
        },

        // 中台心傳區域 — 空白頁
        {
          path: ROUTES.TRANSMISSION_AREA_PATTERN,
          element: <TransmissionAreaPage />,
        },

        // ========== 使用 PageLayout 的頁面 ==========
        {
          element: <PageLayoutWrapper />,
          children: [
            // 登入頁面
            { path: ROUTES.LOGIN, element: <LoginPage /> },
            // 註冊頁面
            { path: ROUTES.REGISTER, element: <RegisterPage /> },
            // 傳燈活動主頁
            { path: ROUTES.WISDOM_LAMP, element: <WisdomLampPage /> },
            // 影片集
            { path: ROUTES.VIDEO_COLLECTION, element: <VideoCollectionPage /> },
            // 精舍報導列表
            { path: ROUTES.BRANCH_REPORTS, element: <BranchReportsPage /> },
            // 線上祈福點燈表單
            { path: ROUTES.LAMP_LIGHTING, element: <LampLightingFormPage /> },
            // 光照大千佈告欄
            { path: ROUTES.LAMP_BULLETIN, element: <LampBulletinPage /> },
            // 祈福結果頁
            { path: ROUTES.BLESSING_RESULT, element: <BlessingResultPage /> },
            // 明信片製作（需登入）
            { path: ROUTES.POSTCARD_CREATE, element: <RequireAuth><PostcardCreatePage /></RequireAuth> },
            // 我的明信片列表（需登入）
            { path: ROUTES.POSTCARD_MY, element: <RequireAuth><PostcardMyPage /></RequireAuth> },
            // 個人資訊
            { path: ROUTES.PROFILE, element: <ProfilePage /> },

            // 心理測驗護法DNA
            { path: ROUTES.DNA, element: <DnaListPage /> },
            { path: ROUTES.DNA_QUESTION, element: <DnaQuestionPage /> },
            { path: ROUTES.DNA_ANSWER, element: <DnaAnswerPage /> },

            //---------------------------------------------------------------------------
            // 教理問答
            { path: ROUTES.QUIZ, element: <QuizListPage /> },
            { path: ROUTES.QUIZ_QUESTION_PATTERN, element: <QuizQuestionPage /> },
            { path: ROUTES.QUIZ_ANSWER, element: <QuizAnswerPage /> },

            //---------------------------------------------------------------------------
            // 禪心連線（實況留言板）
            { path: ROUTES.LIVE_COMMENT, element: <BoardListPage /> },
            { path: ROUTES.LIVE_COMMENT_BOARD_PATTERN, element: <TopicListPage /> },
            { path: ROUTES.LIVE_COMMENT_TOPIC_PATTERN, element: <CommentBoardPage /> },

            //---------------------------------------------------------------------------
            // QR Code活動簽到
            { path: ROUTES.QR_CODE, element: <QrcodePage /> },

            //---------------------------------------------------------------------------
            // 兌換
            { path: ROUTES.REDEEM, element: <RedeemPage /> },

            //---------------------------------------------------------------------------
            // 覺光庭影（會員互動影片區）
            { path: ROUTES.JUE_GUANG, element: <JueGuangLandingPage /> },
            { path: ROUTES.JUE_GUANG_TOPIC_PATTERN, element: <JueGuangPage /> },

            //---------------------------------------------------------------------------
            // 中台心傳
            { path: ROUTES.TRANSMISSION, element: <TransmissionListPage /> },

            //---------------------------------------------------------------------------
            // 發願樹
            { path: ROUTES.WISH_TREE, element: <WishTreePage /> },

            //---------------------------------------------------------------------------
            // 護博會
            { path: ROUTES.GUARDIAN_EXPO, element: <GuardianExpoPage /> },
            { path: ROUTES.GUARDIAN_EXPO_LECTURE, element: <GuardianExpoLecturePage /> },
            { path: ROUTES.GUARDIAN_EXPO_MARKET, element: <GuardianExpoMarketPage /> },
            { path: ROUTES.GUARDIAN_EXPO_CHALLENGE, element: <GuardianExpoChallengePage /> },

          ],
        },
      ],
    },
  ]);

// Helper to wrap LandingPage with auth state
function LandingPageWrapper() {
  const [user, setUser] = useState(() => getUserInfo());
  const [memberNotification, setMemberNotification] = useState('');
  const prevIsLoggedIn = useRef(!!getUserInfo());

  useEffect(() => {
    const checkAuth = () => {
      const newUser = getUserInfo();
      const newIsLoggedIn = !!newUser;
      setUser(newUser);
      if (newIsLoggedIn !== prevIsLoggedIn.current) {
        prevIsLoggedIn.current = newIsLoggedIn;
        if (!FEATURE_FLAGS.MEMBER_NOTIFICATION.enabled) return;
        checkMemberNotification().then(({ show_notification, notification }) => {
          setMemberNotification(show_notification && notification ? notification : '');
        }).catch(() => { });
      }
    };

    window.addEventListener('auth-change', checkAuth);
    return () => window.removeEventListener('auth-change', checkAuth);
  }, []);

  useEffect(() => {
    if (!FEATURE_FLAGS.MEMBER_NOTIFICATION.enabled) return;
    checkMemberNotification().then(({ show_notification, notification }) => {
      setMemberNotification(show_notification && notification ? notification : '');
    }).catch(() => { });
  }, []);

  return (
    <>
      <LandingPage user={user} memberNotification={memberNotification} onDismissNotification={() => setMemberNotification('')} />
      <FallingRewardMount />
    </>
  );
}

export default router;
